import { useFormContext } from 'react-hook-form';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { FormContainer } from 'components/layout';
import { Box, Typography } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { Checkbox, ChipInput } from 'components/common';
import { useCampaignsData } from '../hooks';
import CreateCampaignButton from './CreateCampaignButton';
import datadogActions from 'lib/datadog';

const CAMPAIGNS_LIMIT = 20;
const EMPTY_SELECTION: string[] = [];

const DataGridFooter = ({ selectedCount }: { selectedCount: number }) => {
  if (!selectedCount) return null;

  return (
    <Box
      sx={(theme) => ({
        p: 2,
        height: 54,
        minHeight: 54,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
        cursor: 'default',
      })}
    >
      {selectedCount === CAMPAIGNS_LIMIT ? (
        <>
          <InfoIcon color="primary" sx={{ fontSize: 21 }} />
          <Typography variant="body2">
            {`Up to ${CAMPAIGNS_LIMIT} campaigns can be selected`}
          </Typography>
        </>
      ) : (
        <Typography variant="body2">
          {selectedCount} rows selected
        </Typography>
      )}
    </Box>
  );
};

const DataGridCampaign = () => {
  const { watch, setValue, trigger } = useFormContext();
  const selectedCampaigns =
    (watch('selectedCampaigns') as string[] | undefined) ?? EMPTY_SELECTION;

  const COLUMNS = useMemo(() => [
    { field: 'campaign_id', headerName: 'Campaign ID', width: 160 },
    { field: 'campaign_name', headerName: 'Campaign Name', width: 750 },
  ], []);

  const { gridRows, isLoading, isError, error } = useCampaignsData();
  const visibleCampaignIds = useMemo(
    () => new Set(gridRows.map((row) => row.campaign_id)),
    [gridRows],
  );
  const hiddenSelectedCampaigns = useMemo(
    () => selectedCampaigns.filter((id) => !visibleCampaignIds.has(id)),
    [selectedCampaigns, visibleCampaignIds],
  );
  const safeSelectionModel = useMemo(
    () => selectedCampaigns.filter((id) => visibleCampaignIds.has(id)),
    [selectedCampaigns, visibleCampaignIds],
  );

  const handleSelectionMode = useCallback(
    (selectedRows: GridRowSelectionModel) => {
      if (!gridRows) return;
      const parsedSelectedRows = selectedRows
        .slice(0, CAMPAIGNS_LIMIT)
        .map((id) => String(id));
      const hiddenSet = new Set(hiddenSelectedCampaigns);
      const mergedSelection = [
        ...hiddenSelectedCampaigns,
        ...parsedSelectedRows.filter((id) => !hiddenSet.has(id)),
      ].slice(0, CAMPAIGNS_LIMIT);

      setValue('selectedCampaigns', mergedSelection, { shouldValidate: true });
      setValue('selectedAdGroups', [], { shouldValidate: true });
    },
    [setValue, gridRows, hiddenSelectedCampaigns],
  );

  useEffect(() => {
    void trigger('selectedCampaigns');
  }, [trigger]);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    datadogActions.trackDatadogError(error as Error, {
      source: 'tiktok_choose_campaign_query',
    });
  }, [isError, error]);

  return (
    <Box sx={{ flex: 1, height: '100%', width: '100%', position: 'relative' }}>
      <Box style={{ position: 'absolute', left: 0, top: 0, overflow: 'auto', height: '100%', width: '100%' }}>
        <DataGrid
          rows={gridRows}
          columns={COLUMNS}
          checkboxSelection
          rowSelectionModel={safeSelectionModel}
          loading={isLoading}
          onRowSelectionModelChange={handleSelectionMode}
          hideFooterPagination
          isRowSelectable={(params) =>
            selectedCampaigns.length < CAMPAIGNS_LIMIT ||
            selectedCampaigns.includes(params.row.campaign_id)
          }
          slots={{
            footer: () => <DataGridFooter selectedCount={selectedCampaigns.length} />,
          }}
          getRowId={(params) => params.campaign_id}
        />
      </Box>
    </Box>
  );
};

const ChooseCampaign: FC = memo(() => {
  const { formState: { errors } } = useFormContext();
  const isCampaignFieldError = errors?.selectedCampaigns?.message as string;

  return (
    <FormContainer
      title={'Choose Campaigns'}
      error={isCampaignFieldError}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flex: 1,
            flexDirection: 'row',
            alignItems: 'start',
            flexWrap: 'nowrap',
          }}
        >
          <ChipInput
            name="filterCampaignsByName"
            inputLabel="Enter campaign name"
          />
          <Checkbox
            dataTestId={'selectedCampaignsOnly'}
            name={'selectedCampaignsOnly'}
            label={'Selected only'}
            controlLabelSx={{ whiteSpace: 'nowrap' }}
          />
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <CreateCampaignButton />
        </Box>
      </Box>
      <DataGridCampaign />
    </FormContainer>
  );
});
ChooseCampaign.displayName = 'ChooseCampaign';

export default ChooseCampaign;